// ── programs-write: honest write-error → message + posture helpers ──────────
// Wave 2 (Programs). Shared by every program write flow (create / edit /
// publish / archive). A write failure is surfaced INLINE — never swallowed,
// never faked — and a gate / permission-denied / unavailable write reads as its
// honest message, NEVER an implied success. The single CO_* → posture map is
// reused so the modals speak the exact same vocabulary as the list/detail reads.

import { ApiClientError } from "../../services/api-client";
import {
  honestGateFromError,
  postureForCoachingError,
  referenceFromError
} from "../../services/coaching-error-posture";

export interface WriteErrorView {
  tone: "error" | "warning";
  message: string;
}

/**
 * Map a write error to an honest, non-leaking top-level message. A closed gate
 * (gate-closed) explicitly states nothing was changed, so a refused write can
 * never imply success (no-fabrication release gate). The support reference is
 * surfaced verbatim for triage; the message is never a stack or a secret.
 */
export function describeProgramWriteError(error: unknown): WriteErrorView | null {
  if (error == null) return null;
  if (!(error instanceof ApiClientError)) {
    return { tone: "error", message: "Couldn't save the program. Please try again." };
  }
  const posture = postureForCoachingError(error);
  const reference = referenceFromError(error);
  const ref = reference ? ` (reference: ${reference})` : "";

  if (posture === "permission-denied") {
    return { tone: "error", message: "Your role can't make this change. Ask a super admin if you need it." };
  }
  if (posture === "gate-closed") {
    const gate = honestGateFromError(error);
    return {
      tone: "warning",
      message: `${gate?.reason ?? "This action is closed right now."} No change was made.`
    };
  }
  if (posture === "unavailable") {
    return { tone: "error", message: "Coaching is temporarily unavailable. Try again in a moment." };
  }
  // validation / not-found / generic — show the server message + reference.
  return { tone: "error", message: `${error.message}${ref}` };
}

// ── The program lifecycle status values create/edit may WRITE ──────────────
// State machine: draft -> active (publish) -> archived (archive = PATCH status).
// Reads may emit a WIDER set than create accepts (no DB CHECK on the status
// column — blueprint §1 trap 5), so detail/columns humanize ANY status, but the
// create/edit form only ever WRITES one of these three.
export const PROGRAM_WRITE_STATUSES = ["draft", "active", "archived"] as const;
export type ProgramWriteStatus = (typeof PROGRAM_WRITE_STATUSES)[number];

/** The two statuses the list/filter exposes as the common operational states. */
export const PROGRAM_FILTER_STATUSES = ["draft", "active", "archived"] as const;
